import { useMemo } from 'react'
import {
  PAYMENT_METHOD_CONFIGS,
  PaymentMethodConfig,
  paymentMethodMap,
} from 'modules/flow/components/payment-methods-step/utils/payment-method-configs'
import { PaymentMethod, PaymentProcessor } from 'models/ChargeIntent'
import useConfig from 'modules/config/state/use-config'
import useOrder from 'modules/order/state/order/use-order'
import { shouldShowDW } from 'modules/order/state/payment-details/payment-details-utils'
import { UAParser } from 'ua-parser-js'
import { FREQUENCY } from '@classy/campaign-page-blocks'

// Main hook to fetch active payment method configurations
export const useActivePaymentMethodConfigs = (): PaymentMethodConfig[] => {
  const config = useConfig()
  const order = useOrder()

  // Get relevant states
  const isVenmoEnabled = _useIsVenmoEnabled()
  const isDWEnabled = shouldShowDW(config) && order.stripePaymentRequest.canMakePayment
  const isACHEnabled = config.planFeatures.ach_account_and_routing
  const walletType = order.stripePaymentRequest.walletType

  // Memoize the calculation of active payment methods
  return useMemo(() => {
    const seenTypes: { [key: string]: boolean } = {}

    const unsortedConfigs = config.payment.activeMethods.reduce<PaymentMethodConfig[]>(
      (result, paymentMethod) => {
        let paymentType = paymentMethod.type

        if (seenTypes[paymentType]) {
          return result
        }

        if (
          paymentType === PaymentMethod.PayPal_Commerce &&
          paymentMethod.name === 'PAYPAL_COMMERCE'
        ) {
          paymentType = PaymentMethod.PayPal_Commerce
        }

        if (paymentType === PaymentMethod.PayPal && paymentMethod.name === 'PAYPAL') {
          paymentType = PaymentMethod.PayPal
        }

        // Conditionally filter out Venmo
        if (paymentType === PaymentMethod.Venmo && !isVenmoEnabled) {
          return result
        }

        // use Plaid for ACH unless org settings enable non-Plaid ACH
        if (paymentType === PaymentMethod.ACH && !isACHEnabled) {
          paymentType = PaymentMethod.Plaid
        }

        // Conditionally filter out DW
        if (paymentType === PaymentMethod.DW && !isDWEnabled) {
          return result
        }

        seenTypes[paymentType] = true

        const config = getPaymentMethodConfig(paymentType, walletType)

        result.push({
          ...config,
          key: paymentType,
          processor: paymentMethod.name as PaymentProcessor,
        })

        return result
      },
      [],
    )

    // Sort the configs by the order attribute
    return unsortedConfigs.sort((a, b) => a.order - b.order)
  }, [config.payment.activeMethods, isVenmoEnabled, isACHEnabled, isDWEnabled, walletType])
}

// Utility function to get the config for a given payment method
export const getPaymentMethodConfig = (
  paymentMethod: PaymentMethod,
  walletType: string | null,
): PaymentMethodConfig => {
  const effectiveWalletType = paymentMethod === PaymentMethod.DW ? walletType : null

  return effectiveWalletType
    ? PAYMENT_METHOD_CONFIGS[effectiveWalletType]
    : PAYMENT_METHOD_CONFIGS[paymentMethodMap[paymentMethod]]
}

// Utility hook to check if Venmo is enabled
const _useIsVenmoEnabled = (): boolean => {
  const order = useOrder()

  // Parse the user's OS and browser information
  const parser = new UAParser()
  const os = parser.getOS()?.name ?? ''
  const browser = parser.getBrowser()?.name ?? ''

  // Memoize Venmo check logic
  return useMemo(() => {
    // Venmo is not supported for recurring payments
    if (order.frequency.value !== FREQUENCY.ONE_TIME) {
      return false
    }

    // Venmo is only supported on iOS with Mobile Safari and Android with Chrome
    return !(
      (os === 'iOS' && browser !== 'Mobile Safari') ||
      (os === 'Android' && !browser?.toLowerCase().includes('chrome'))
    )
  }, [order.frequency.value, os, browser])
}
